// Sentry error tracking — gated by EXPLICIT consent (`errorReports: true` v localStorage).
// Init NIE pri load. CookieBanner „Rozumiem" / „Odmietnuť" tento záznam nenastavujú
// (nič mimo nevyhnutného sa nezapína), takže v prehliadači sa Sentry dnes nespúšťa.
//
// PostHog NEpouzivame — analytics riesi vlastny lib/analytics.js + Supabase
// `analytics_events` tabulka (opt-in benchmarking).
//
// SDK sa naťahuje LEN dynamicky (`import('@sentry/react')`), nikdy staticky na
// top-level — statický import naťahoval 77KB (24 % hlavného balíka) VŽDY, aj
// bez súhlasu (namerané source-map-explorer 2026-09-28, bead MasterProject-0fl7).
// SentryErrorBoundary preto NIE JE Sentry.ErrorBoundary — je to vlastný ľahký
// boundary nižšie, ktorý nahlási chybu cez captureException LEN ak je SDK už
// načítaný (rovnaký princíp ako captureException/addBreadcrumb/captureMessage).
//
// Decision: docs/decisions/2026-05-08-sentry-error-tracking.md

import React from 'react';

const COOKIE_CONSENT_KEY = 'cd:cookie-consent-v1';

let Sentry = null;
let sentryLoading = null; // in-flight import(), aby sa SDK nenačítal 2×
let sentryInited = false;

function hasConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    return Boolean(raw) && JSON.parse(raw)?.errorReports === true;
  } catch {
    return false;
  }
}

function redactQueryString(url) {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url, window.location.origin);
    const SENSITIVE = ['email', 'ico', 'dic', 'code', 'token', 'access_token', 'refresh_token'];
    SENSITIVE.forEach((key) => {
      if (u.searchParams.has(key)) u.searchParams.set(key, '[Filtered]');
    });
    return u.toString();
  } catch {
    return url;
  }
}

function scrubPii(event) {
  if (!event) return event;

  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }

  if (event.request) {
    if (event.request.headers) {
      delete event.request.headers.cookie;
      delete event.request.headers.Cookie;
      delete event.request.headers.authorization;
      delete event.request.headers.Authorization;
    }
    if (event.request.url) {
      event.request.url = redactQueryString(event.request.url);
    }
    if (event.request.query_string && typeof event.request.query_string === 'string') {
      event.request.query_string = redactQueryString(`?${event.request.query_string}`).replace(/^.*\?/, '');
    }
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs.forEach((b) => {
      if (b?.data?.url) b.data.url = redactQueryString(b.data.url);
    });
  }

  return event;
}

export async function initSentry() {
  if (sentryInited) return;

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console -- intentional warning, gated by NODE_ENV
      console.warn('[observability] REACT_APP_SENTRY_DSN missing, Sentry skipped.');
    }
    return;
  }

  if (!sentryLoading) sentryLoading = import('@sentry/react');
  Sentry = await sentryLoading;

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || 'production',
    release: 'todue@2026-05-08',
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    maxBreadcrumbs: 30,
    ignoreErrors: [
      /ResizeObserver loop/,
      /Non-Error promise rejection captured/,
      'Network request failed',
      'AbortError',
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.dedupeIntegration(),
    ],
    beforeSend(event) {
      return scrubPii(event);
    },
  });

  sentryInited = true;
}

export async function maybeInitSentryFromConsent() {
  if (hasConsent()) await initSentry();
}

export function captureException(error, context) {
  if (!sentryInited || !Sentry) return;
  Sentry.captureException(error, context);
}

export function addBreadcrumb(data) {
  if (!sentryInited || !Sentry) return;
  Sentry.addBreadcrumb(data);
}

export function captureMessage(message, opts) {
  if (!sentryInited || !Sentry) return;
  Sentry.captureMessage(message, opts);
}

export class SentryErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    captureException(error, { extra: { componentStack: info?.componentStack } });
  }

  resetError = () => this.setState({ hasError: false });

  render() {
    if (this.state.hasError) {
      const Fallback = this.props.fallback;
      return Fallback ? <Fallback resetError={this.resetError} /> : null;
    }
    return this.props.children;
  }
}
